/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI, statsAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';
import logger from '../utils/logger';
import { 
  Plus, 
  FolderOpen, 
  BarChart3, 
  CheckCircle2,
  Clock,
  ArrowRight,
  Building,
  Home,
  Store
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { motion } from 'framer-motion';

const projectTypeIcons = {
  residential: Home,
  mixed_use: Building,
  commercial: Store
};

// Extracted outside component to prevent recreation on each render
const StatCard = ({ icon: Icon, label, value, color = 'accent' }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="text-3xl font-heading font-bold">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded flex items-center justify-center bg-${color}/10`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  const projectTypeLabels = {
    residential: t('newProject.residential'),
    mixed_use: t('newProject.mixed'),
    commercial: t('newProject.commercial')
  };

  const statusLabels = {
    draft: { label: t('project.status.draft'), color: 'secondary' },
    analyzed: { label: t('project.status.analyzed'), color: 'default' },
    completed: { label: language === 'fr' ? 'Complété' : 'Completed', color: 'default' }
  };

  const loadData = useCallback(async () => {
    try {
      const [projectsRes, statsRes] = await Promise.all([
        projectsAPI.list(),
        statsAPI.get()
      ]);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      logger.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map((i) => (
            <Skeleton key={`skeleton-${i}`} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl uppercase text-primary">
            {t('dashboard.title')}
          </h1>
          <p className="text-muted-foreground">
            {language === 'fr' ? "Gérez vos projets d'analyse de zonage" : "Manage your zoning analysis projects"}
          </p>
        </div>
        <Link to="/projects/new">
          <Button className="btn-primary" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('nav.newProject')}
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatCard
            icon={FolderOpen}
            label={t('dashboard.stats.totalProjects')}
            value={stats?.total_projects || 0}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatCard
            icon={CheckCircle2}
            label={t('dashboard.stats.analyzed')}
            value={stats?.analyzed_projects || 0}
            color="success"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatCard
            icon={Clock}
            label={t('dashboard.stats.draft')}
            value={stats?.draft_projects || 0}
            color="warning"
          />
        </motion.div>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="font-heading uppercase flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-accent" />
            {t('dashboard.recentProjects')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {projects.length === 0 ? (
            <div className="text-center py-16">
              <FolderOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-muted-foreground mb-2">
                {t('dashboard.noProjects')}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t('dashboard.createFirst')}
              </p>
              <Link to="/projects/new">
                <Button className="btn-primary" data-testid="empty-new-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Créer un projet' : 'Create a project'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y">
              {projects.map((project, index) => {
                const TypeIcon = projectTypeIcons[project.project_type] || Building;
                const status = statusLabels[project.status] || statusLabels.draft;
                
                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link 
                      to={`/projects/${project.id}`}
                      className="flex items-center gap-6 p-6 hover:bg-secondary/50 transition-colors group"
                      data-testid={`project-${project.id}`}
                    >
                      <div className="w-12 h-12 bg-primary/5 rounded flex items-center justify-center flex-shrink-0">
                        <TypeIcon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-heading font-semibold text-lg truncate">
                            {project.name}
                          </h3>
                          <Badge variant={status.color}>
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {project.property?.address} • {project.property?.arrondissement}
                        </p>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium">
                          {projectTypeLabels[project.project_type]}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(project.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                        </p>
                      </div>

                      <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-accent transition-colors" />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
